"use client"

import Image from "next/image"
import { motion } from "framer-motion"
import { fadeIn } from "@/components/motion"
import { Play } from "lucide-react"
import { useRef, useState } from "react"

export default function VideoSection() {
 const videoRef = useRef<HTMLVideoElement>(null)
 const [isPlaying, setIsPlaying] = useState(false)

 const handleWatchNow = () => {
   const video = videoRef.current
   if (!video) return
   setIsPlaying(true)
   video.play().catch(() => {
     // Autoplay can be blocked; controls are visible so the user can retry.
   })
 }

 return (
  <section className="text-white relative overflow-hidden py-12 lg:py-16">

   {/* Grunge background — behind content */}
   <div className="absolute inset-0 z-0">
    <Image
     src="/images/Grunge background.jpg"
     alt=""
     fill
     className="object-cover opacity-100"
     priority={false}
    unoptimized={true}
    sizes="100vw"
    style={{ objectFit: "cover" }}
    />
   </div>

   {/* Character — absolute bottom-right, no bottom padding */}
   <motion.div
    className="absolute bottom-0 right-8 md:right-16 lg:right-20 z-30 pointer-events-none"
    variants={fadeIn}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-100px" }}
   >
    <div className="relative w-44 h-56 md:w-56 md:h-72 lg:w-64 lg:h-80">
     <Image
      src="/images/watch-the-movement-avatar.png"
      alt="Bully Talk character waving and holding a HEY! tablet"
      fill
      className="object-contain object-bottom"
     />
    </div>
   </motion.div>

   <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
    <div className="grid lg:grid-cols-2 gap-8 items-center">

     {/* Left Column: Text & CTA */}
     <motion.div
      className="flex flex-col items-start relative z-10"
      variants={fadeIn}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
     >
      <h2 className="text-4xl sm:text-5xl lg:text-7xl font-antonio font-bold leading-[0.9] tracking-tight uppercase text-white">
       WATCH THE <span className="text-white block mt-2">MOVEMENT</span>
      </h2>
      <p className="mt-6 text-lg sm:text-xl text-white max-w-md font-normal">
       See Bully Talk in action and how we're creating change in schools and communities every day.
      </p>

      <button
       type="button"
       onClick={handleWatchNow}
       className="mt-10 bg-white text-black px-10 py-6 text-lg font-antonio font-bold rounded-none uppercase tracking-widest shadow-[6px_6px_0px_0px_#5A3290] flex items-center gap-2 transition-all hover:bg-[#5A3290] hover:text-white hover:-translate-y-1 group"
      >
       <Play className="w-5 h-5 fill-black group-hover:fill-white transition-colors" />
       WATCH NOW
      </button>
     </motion.div>

     {/* Right Column: Video Player */}
     <motion.div
      className="relative z-10 overflow-hidden outline outline-4 outline-white"
      variants={fadeIn}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
     >
      <video
       ref={videoRef}
       src="/videos/Movement (1).mp4"
       controls={isPlaying}
       className="w-full aspect-video object-cover"
       preload="metadata"
       playsInline
      >
       Your browser does not support the video tag.
      </video>

      {!isPlaying && (
       <button
        type="button"
        onClick={handleWatchNow}
        aria-label="Play video"
        className="absolute inset-0 z-20 flex items-center justify-center bg-black/30 transition-colors hover:bg-black/40 group"
       >
        <span className="bg-white text-black w-20 h-20 md:w-24 md:h-24 flex items-center justify-center shadow-[6px_6px_0px_0px_#5A3290] transition-all group-hover:bg-[#5A3290] group-hover:shadow-[6px_6px_0px_0px_white] group-hover:-translate-y-1">
         <Play className="w-9 h-9 md:w-12 md:h-12 fill-black group-hover:fill-white transition-colors" />
        </span>
       </button>
      )}
     </motion.div>

    </div>
   </div>
  </section>
 )
}